import { getSandboxUrl } from "./sandboxUrl";
import { retrieveAuthToken } from "../utils";

import { AuthClient, DirectoryClient, ProctorClient } from "@securityjourney/frontend-lib-svc";
export type { User, UserProfile } from "@securityjourney/frontend-lib-svc";
import { ChoresClient } from "./svcChores";

// Export some global types
export { ServiceError } from "@securityjourney/service-client";

// Clients from imported libraries
import { SandboxClient } from "@securityjourney/svc-sandbox";
export type {
  SandboxStatus,
  SandboxCode,
  TestStatus,
  SandboxCredentials,
} from "@securityjourney/svc-sandbox";

import { LessonEventsClient } from "@securityjourney/svc-lesson";
export type {
  LessonEventsClient,
  HintUsageResponse,
  CtfSubmissionResponse,
} from "@securityjourney/svc-lesson";

import { ScormClient } from "@securityjourney/svc-scorm";

import { IntegrationsClient } from "./svcIntegrationsClient";
import { ExperimentClient } from "./svcExperimentClient";
export type {
  CWE,
  CWEsResponse,
  CWEFilterOptions,
  IntegrationsClient,
} from "./svcIntegrationsClient";
export type {
  GuardianClient,
  ScannerType,
  ScanStatus,
  ScannersResponse,
  MetricsSummaryResponse,
  ScanMetricsSummary,
  ScanRecord,
  ScanHistoryResponse,
  TokenUsage,
} from "./svcGuardianClient";
export { getGuardianClient } from "./svcGuardianClient";
import { IdpClient, IdpAdminClient, LoginClient } from "./svcIdp";
export type {
  ProctorClient as ProctorAssessmentClient,
  ExamInstance,
  ExamConfiguration,
  ExamFilters,
  ExamQuestion,
  ExamAnswer,
  ExamResult,
  ExamSections,
} from "./svcProctor";

// Singleton instances of each client

let authClientInstance: AuthClient | null = null;
let choreClientInstance: ChoresClient | null = null;
let directoryClientInstance: DirectoryClient | null = null;
let idpClientInstance: IdpClient | null = null;
let proctorClientInstance: ProctorClient | null = null;
let sandboxClientInstance: SandboxClient | null = null;
let integrationsClientInstance: IntegrationsClient | null = null;
let scormClientInstance: ScormClient | null = null;
let lessonEventsClientInstance: LessonEventsClient | null = null;
let experimentClientInstance: ExperimentClient | null = null;
let idpAdminClientInstance: IdpAdminClient | null = null;
let loginClientInstance: LoginClient | null = null;

// Exported functions to get singleton instances of each client
export const getLessonEventsClient = (): LessonEventsClient => {
  if (!lessonEventsClientInstance) {
    lessonEventsClientInstance = new LessonEventsClient(retrieveAuthToken);
  }
  return lessonEventsClientInstance;
};

export const getScormClient = (headers?: Record<string, string>): ScormClient => {
  if (!scormClientInstance) {
    scormClientInstance = new ScormClient({ headers });
  }
  return scormClientInstance;
};

/**
 * Authorized ScormClient with auth headers.
 */
export const getAuthorizedScormClient = async (): Promise<ScormClient> => {
  const token = await retrieveAuthToken();
  return new ScormClient({
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getAuthClient = (): AuthClient => {
  if (!authClientInstance) {
    authClientInstance = new AuthClient();
  }
  return authClientInstance;
};

export const getChoresClient = (): ChoresClient => {
  if (!choreClientInstance) {
    choreClientInstance = new ChoresClient();
  }
  return choreClientInstance;
};

export const getDirectoryClient = (): DirectoryClient => {
  if (!directoryClientInstance) {
    directoryClientInstance = new DirectoryClient(retrieveAuthToken);
  }
  return directoryClientInstance;
};

export const getIdpClient = (): IdpClient => {
  if (!idpClientInstance) {
    idpClientInstance = new IdpClient();
  }
  return idpClientInstance;
};

export const getIdpAdminClient = (): IdpAdminClient => {
  if (!idpAdminClientInstance) {
    idpAdminClientInstance = new IdpAdminClient();
  }
  return idpAdminClientInstance;
};

export const getLoginClient = (): LoginClient => {
  if (!loginClientInstance) {
    loginClientInstance = new LoginClient();
  }
  return loginClientInstance;
};

export const getProctorClient = (): ProctorClient => {
  if (!proctorClientInstance) {
    proctorClientInstance = new ProctorClient(retrieveAuthToken);
  }
  return proctorClientInstance;
};

export const getSandboxClient = (): SandboxClient => {
  const sandboxUrl = getSandboxUrl();
  if (!sandboxClientInstance) {
    sandboxClientInstance = new SandboxClient(retrieveAuthToken, sandboxUrl);
  }
  return sandboxClientInstance;
};

export const getIntegrationsClient = (): IntegrationsClient => {
  if (!integrationsClientInstance) {
    integrationsClientInstance = new IntegrationsClient(retrieveAuthToken);
  }
  return integrationsClientInstance;
};

export const getExperimentClient = (): ExperimentClient => {
  if (!experimentClientInstance) {
    experimentClientInstance = new ExperimentClient(retrieveAuthToken);
  }
  return experimentClientInstance;
};
